import { ReactNode } from "react";
import { cn } from "../../utils/cn";

export interface FlowCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  fileTypes: string[];
  selected?: boolean;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

export default function FlowCard({
  title,
  description,
  icon,
  fileTypes,
  selected = false,
  onClick,
  className,
  disabled = false,
}: FlowCardProps) {
  return (
    <div
      className={cn(
        "group relative w-full rounded-xl transition-all duration-300 cursor-pointer overflow-hidden",
        selected 
          ? "p-[2px] bg-gradient-to-r from-[#3C6AC6] via-[#D216CC] to-[#FB6B96]" 
          : "p-[2px] bg-gradient-to-br from-gray-900/95 to-black/95 hover:bg-gradient-to-r hover:from-[#3C6AC6] hover:via-[#D216CC] hover:to-[#FB6B96] hover:shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1",
        disabled && "opacity-50 cursor-not-allowed hover:transform-none hover:shadow-none",
        className
      )}
      onClick={disabled ? undefined : onClick}
    >
      <div className={cn(
        "w-full h-full rounded-xl p-6 transition-all duration-300",
        selected 
          ? "bg-gradient-to-br from-gray-900/95 to-black/95 shadow-lg shadow-blue-500/20"
          : "bg-gradient-to-br from-gray-900/95 to-black/95"
      )}>
        {/* Background glow effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Content */}
        <div className="relative flex flex-col items-center text-center space-y-4">
          {/* Icon Container */}
          <div className={cn(
            "w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300",
            selected 
              ? "bg-gradient-to-br from-gray-800/60 to-gray-900/60 shadow-lg shadow-blue-500/20"
              : "bg-gradient-to-br from-gray-800/60 to-gray-900/60"
          )}>
            <div className={cn(
              "w-8 h-8 transition-colors duration-300",
              selected ? "text-blue-400" : "text-slate-300 group-hover:text-blue-400"
            )}>
              {icon}
            </div>
          </div>
          
          {/* Title and Description */}
          <div className="space-y-3">
            <h3 className={cn(
              "font-semibold text-xl transition-colors duration-300",
              selected ? "text-white" : "text-white group-hover:text-blue-100"
            )}>
              {title}
            </h3>
            <p className={cn(
              "text-base leading-relaxed transition-colors duration-300",
              selected ? "text-slate-200" : "text-slate-400 group-hover:text-slate-300"
            )}>
              {description}
            </p>
          </div>
          
          {/* File Type Tags */}
          <div className="flex flex-wrap gap-2 justify-center">
            {fileTypes.map((type, index) => (
              <span
                key={index}
                className={cn(
                  "px-3 py-1 text-xs rounded-full font-medium transition-all duration-300",
                  selected 
                    ? "bg-gray-800/50 text-slate-400 border border-gray-700/30"
                    : "bg-gray-800/50 text-slate-400 border border-gray-700/30"
                )}
              >
                {type}
              </span>
            ))}
          </div>
          
          {/* Arrow indicator */}
          {!disabled && (
            <div className="w-8 h-8 rounded-full bg-gray-800/50 flex items-center justify-center group-hover:bg-blue-500/20 transition-all duration-300">
              <svg className="w-4 h-4 text-slate-400 group-hover:text-blue-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 